import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClockCounterClockwiseIcon } from '@phosphor-icons/react/dist/csr/ClockCounterClockwise';
import { CardHead } from '../../design-system/composites/Card/Card';
import { ArrowFatLineUpIcon } from '@phosphor-icons/react/dist/csr/ArrowFatLineUp';
import { ArrowLeftIcon } from '@phosphor-icons/react/dist/csr/ArrowLeft';
import { ChatCircleTextIcon } from '@phosphor-icons/react/dist/csr/ChatCircleText';
import { CheckCircleIcon } from '@phosphor-icons/react/dist/csr/CheckCircle';
import { LockSimpleIcon } from '@phosphor-icons/react/dist/csr/LockSimple';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';

import { EscalationActionDialog } from './EscalationActionDialog';
import {
  EVENT_ACTION_LABEL_KEY,
  STATUS_LABEL_KEY,
  ACTION_BUTTON_LABEL_KEY,
  type EscalationActionKind,
} from './escalationLabels';
import { Button } from '../../design-system/primitives/Button/Button';
import { ApiError } from '../../lib/api/client';
import { escalationsApi, type EscalationRow } from '../../lib/api/escalations';
import { formatTimestamp } from '../../lib/format/dateTime';
import styles from './EscalationDetail.module.css';

/**
 * The escalation detail view (M-10, Task 5, spec §3) — the record and its
 * event timeline, THE POINT OF THE FEATURE (spec E-2: `escalation_events`
 * replaces `escalations_his`, a snapshot table with no key, no actor and no
 * timestamp of its own). A detail screen without the timeline is a form
 * with extra steps.
 *
 * ## Actions come from the server's state, never re-derived here (Global Constraint)
 *
 * The four transition buttons are gated on the SESSION'S OWN permission
 * (`escalation.act` or `escalation.manage`) and on the record's own
 * `status`/`level` — nothing here re-implements `EscalationPolicy::act()`'s
 * "must be the current assignee" rule, which this client cannot evaluate
 * (the session carries a `uuid`, never the integer `assigned_to` id an
 * `EscalationResource` reports — see `client.ts`'s own `ApiUser`). A caller
 * who is not the assignee still sees the button and gets a `403` from the
 * server on click, surfaced by `EscalationActionDialog` exactly as worded —
 * this component only hides the OBVIOUSLY dead controls:
 *
 * - every one of the four, once `status` is `resolved`/`closed` (a terminal
 *   escalation refuses all four with a `409`, spec E-3);
 * - `advance` alone, at `level` 3 (advancing past the end of the chain is
 *   ALWAYS a `409` — `EscalationChainExhaustedException` — regardless of
 *   who is asking, the identical "structural, not assignee-dependent" fact
 *   that makes hiding it here a hidden dead button rather than a predicted
 *   business rule).
 *
 * `resolve`/`close`/`comment` remain offered at every level, including 3 —
 * only `advance` is chain-exhausted there.
 */

export interface EscalationDetailProps {
  uuid: string;
  permissions: readonly string[];
  onBack: () => void;
}

type LoadStatus = 'loading' | 'ready' | 'error';

export function EscalationDetail({ uuid, permissions, onBack }: EscalationDetailProps) {
  const { t, i18n } = useTranslation();
  const canAct =
    permissions.includes('escalation.act') || permissions.includes('escalation.manage');

  const [status, setStatus] = useState<LoadStatus>('loading');
  const [record, setRecord] = useState<EscalationRow | null>(null);
  const [reference, setReference] = useState<string | null>(null);
  const [actionKind, setActionKind] = useState<EscalationActionKind | null>(null);

  const load = useCallback(async () => {
    setStatus('loading');
    setReference(null);

    try {
      const found = await escalationsApi.get(uuid);
      setRecord(found);
      setStatus('ready');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setReference(error.requestId ?? null);
      setStatus('error');
    }
  }, [uuid]);

  useEffect(() => {
    void load();
  }, [load]);

  function handleActionDone(updated: EscalationRow): void {
    setRecord(updated);
    setActionKind(null);
  }

  if (status === 'loading') {
    return (
      <div className={styles['page']}>
        <Button variant="ghost" onClick={onBack} icon={<ArrowLeftIcon size={16} className={styles['backIcon']} />}>
          {t('escalations.back')}
        </Button>
        <p role="status">{t('list.loading')}</p>
      </div>
    );
  }

  if (status === 'error' || record === null) {
    return (
      <div className={styles['page']}>
        <Button variant="ghost" onClick={onBack} icon={<ArrowLeftIcon size={16} className={styles['backIcon']} />}>
          {t('escalations.back')}
        </Button>
        <div role="alert" className={styles['recovery']}>
          <p>{t('escalations.loadFailedDetail')}</p>
          <Button
            variant="secondary"
            onClick={() => {
              void load();
            }}
            icon={<ArrowClockwiseIcon size={16} />}
          >
            {t('escalations.retry')}
          </Button>
          {reference === null ? null : (
            <p className={styles['reference']}>
              {t('auth.supportReference')}{' '}
              <bdi dir="ltr" data-testid="escalationDetailRequestId">
                {reference}
              </bdi>
            </p>
          )}
        </div>
      </div>
    );
  }

  const terminal = record.status === 'resolved' || record.status === 'closed';
  const offerActions = canAct && !terminal;
  const offerAdvance = offerActions && record.level < 3;

  return (
    <div className={styles['page']}>
      <div className={styles['header']}>
        <div className={styles['identity']}>
          <Button variant="ghost" onClick={onBack} icon={<ArrowLeftIcon size={16} className={styles['backIcon']} />}>
            {t('escalations.back')}
          </Button>
          <h2 className={styles['title']}>
            <bdi dir="ltr">{record.mobile}</bdi>
          </h2>
        </div>

        {offerActions ? (
          <div className={styles['actions']}>
            {offerAdvance ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setActionKind('advance');
                }}
                icon={<ArrowFatLineUpIcon size={16} />}
              >
                {t(ACTION_BUTTON_LABEL_KEY.advance)}
              </Button>
            ) : null}
            <Button
              variant="secondary"
              onClick={() => {
                setActionKind('resolve');
              }}
              icon={<CheckCircleIcon size={16} />}
            >
              {t(ACTION_BUTTON_LABEL_KEY.resolve)}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setActionKind('close');
              }}
              icon={<LockSimpleIcon size={16} />}
            >
              {t(ACTION_BUTTON_LABEL_KEY.close)}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setActionKind('comment');
              }}
              icon={<ChatCircleTextIcon size={16} />}
            >
              {t(ACTION_BUTTON_LABEL_KEY.comment)}
            </Button>
          </div>
        ) : null}
      </div>

      <dl className={[styles['panel'], styles['primary'], styles['fields']].join(' ')}>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('table.status')}</dt>
          <dd className={styles['fieldValue']}>{t(STATUS_LABEL_KEY[record.status])}</dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('escalations.level')}</dt>
          <dd className={styles['fieldValue']}>{record.level}</dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('table.caseNumber')}</dt>
          <dd className={styles['fieldValue']}>
            {record.case === null ? (
              (record.case_number ?? t('table.notRecorded'))
            ) : (
              <bdi dir="ltr">{record.case.case_number}</bdi>
            )}
          </dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('escalations.assignedTo')}</dt>
          <dd className={styles['fieldValue']}>{record.assignee_name ?? t('table.notRecorded')}</dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('escalations.fieldReason')}</dt>
          <dd className={styles['fieldValue']}>{record.reason ?? t('table.notRecorded')}</dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('escalations.fieldDescription')}</dt>
          <dd className={styles['fieldValue']}>{record.description ?? t('table.notRecorded')}</dd>
        </div>
        <div className={styles['field']}>
          <dt className={styles['fieldLabel']}>{t('escalations.openedAt')}</dt>
          <dd className={styles['fieldValue']}>
            {record.opened_at === null
              ? t('table.notRecorded')
              : formatTimestamp(record.opened_at, i18n.language)}
          </dd>
        </div>
        {record.resolved_at === null ? null : (
          <div className={styles['field']}>
            <dt className={styles['fieldLabel']}>{t('escalations.resolvedAt')}</dt>
            <dd className={styles['fieldValue']}>
              {formatTimestamp(record.resolved_at, i18n.language)}
            </dd>
          </div>
        )}
        {record.resolution_notes === null ? null : (
          <div className={styles['field']}>
            <dt className={styles['fieldLabel']}>{t('escalations.resolutionNotes')}</dt>
            <dd className={styles['fieldValue']}>{record.resolution_notes}</dd>
          </div>
        )}
      </dl>

      <section className={[styles['panel'], styles['section']].join(' ')}>
        <CardHead level="h3" icon={<ClockCounterClockwiseIcon size={16} weight="duotone" />} title={t('escalations.timelineTitle')} />
        {record.events.length === 0 ? (
          <p className={styles['timelineEmpty']}>{t('escalations.timelineEmpty')}</p>
        ) : (
          <ol className={styles['timeline']}>
            {record.events.map((event) => (
              <li key={event.uuid} className={styles['event']}>
                <div className={styles['eventHeader']}>
                  <span className={styles['eventAction']}>
                    {t(EVENT_ACTION_LABEL_KEY[event.action])}
                  </span>
                  <span className={styles['eventMeta']}>
                    {event.occurred_at === null ? null : (
                      <bdi dir="ltr">{formatTimestamp(event.occurred_at, i18n.language)}</bdi>
                    )}
                  </span>
                </div>
                <div className={styles['eventMeta']}>
                  {event.actor_name === null ? null : (
                    <span>{t('escalations.eventActor', { actor: event.actor_name })}</span>
                  )}
                  {event.from_level === null ||
                  event.to_level === null ||
                  event.from_level === event.to_level ? null : (
                    <span>
                      {t('escalations.eventLevelChange', {
                        from: event.from_level,
                        to: event.to_level,
                      })}
                    </span>
                  )}
                </div>
                {event.notes === null ? null : (
                  <p className={styles['eventNotes']}>{event.notes}</p>
                )}
              </li>
            ))}
          </ol>
        )}
      </section>

      <EscalationActionDialog
        open={actionKind !== null}
        kind={actionKind ?? 'comment'}
        escalationUuid={record.uuid}
        mobile={record.mobile}
        onClose={() => {
          setActionKind(null);
        }}
        onDone={handleActionDone}
      />
    </div>
  );
}
